import React from 'react';
import { cn } from '@/lib/utils';

export interface BudgetBarProps {
  /** How much of the limit is used, as a percentage. Below 0 leaves the bar empty. */
  value: number;
  /** Spent past the limit: the bar is redrawn to the used value, with a tick at the limit and the overflow hatched. */
  over?: boolean;
  className?: string;
}

// Past this the overflow stops growing, so the limit tick never shrinks into the bar's first few pixels.
const OVER_SCALE_CAP = 300;

// Hatching in the foreground ink: reads as "excess", not as a fuller bar — and in dark mode it stays a texture
// rather than the brightest solid on the page.
const HATCH = 'repeating-linear-gradient(135deg, hsl(var(--foreground)) 0 2px, transparent 2px 5px)';

// Not ui/progress: its fill is hard-coded bg-primary, and six amber bars would break the Amber Signal Rule.
// The bar is neutral on purpose — a row's meaning is in its numbers, and the percentage beside it is the
// text alternative, so the bar itself stays out of the accessibility tree. Within the limit it fills in
// proportion. Over it, the whole track is the used value: the solid part ends at a gap that marks the limit and
// the rest is hatched, so 127% shows how far past it went instead of looking like a group that landed on 100%.
// Still monochrome (Semantic Anchor Rule).
export const BudgetBar: React.FC<BudgetBarProps> = ({ value, over = false, className }) => {
  if (over) {
    const limitAt = (100 / Math.min(Math.max(value, 100), OVER_SCALE_CAP)) * 100;
    return (
      <div className={cn('relative flex h-1.5 overflow-hidden rounded-full bg-muted', className)} aria-hidden="true">
        <div className="h-full rounded-l-full bg-foreground/60" style={{ width: `${limitAt}%` }} />
        <div className="h-full w-0.5 shrink-0 bg-background" data-limit-tick="" />
        <div className="h-full flex-1 rounded-r-full opacity-80" style={{ backgroundImage: HATCH }} data-overflow="" />
      </div>
    );
  }

  const width = Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0;
  return (
    <div className={cn('h-1.5 overflow-hidden rounded-full bg-muted', className)} aria-hidden="true">
      <div className="h-full rounded-full bg-foreground/60" style={{ width: `${width}%` }} />
    </div>
  );
};
